import { useCallback, useEffect, useState } from 'react';
import { extractApiError } from '../../utils/errorMessages';
import { requireList } from './paperPricingFormat';

const SEARCH_PAUSE_MS = 300;

/**
 * One master list (benchmarks or paper instruments) with its active / inactive
 * counts, searched on the server (ledger row 132, AC1).
 *
 * The search waits for a short pause in typing, so each keystroke is not a
 * request. Every answer is checked on arrival: a list or count that did not
 * come back is shown as a load problem, never as an empty list.
 *
 * `fetcher` must keep its identity between renders (pass the service function).
 * `checkItem(item)` may throw for a row missing something the screen relies on.
 */
const useMasterList = (fetcher, failureText, checkItem) => {
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => setQuery(search.trim()), SEARCH_PAUSE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        fetcher({ search: query })
            .then((data) => {
                const results = requireList(data, 'results');
                const counts = data.counts;
                if (!counts || typeof counts.active !== 'number' || typeof counts.inactive !== 'number') {
                    throw new Error('The server sent an answer this screen cannot read. Refresh the page and try again.');
                }
                if (checkItem) results.forEach(checkItem);
                if (!cancelled) setList(data);
            })
            .catch((err) => {
                if (cancelled) return;
                setList(null);
                setError(extractApiError(err, failureText));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // checkItem and failureText are fixed per list; the request depends on
        // the fetcher, the search and the reload count only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher, query, attempt]);

    const reload = useCallback(() => setAttempt((count) => count + 1), []);

    return { search, setSearch, list, loading, error, reload };
};

export default useMasterList;
